import React, { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { STATIC_PATH } from "../constants/config";
import {
  BigPlayButton,
  Icons,
  PlayerContainer,
  PlayerControls,
  Seek,
  SeekBar,
  PlayTimer,
  VolumeContainer,
  VolumeSlider,
  BgWrapper,
} from "./animo-component";
import { playPausePlayer, setAnimoFullScreen, setGlobalVolume, setPlayAll, setPlayHeadTime, showFeedbackModal } from "../redux/actions/appUtils";
import { secondsToMinute } from "../containers/timeline/timeline-helper";
import ActionTypes from "../constants/action-types";
import Slider from "../containers/zoom/zoom-slider";
import isIphoneSafari from "../helper/isIphoneSafari";
import useMobileDetection from "../helper/hooks/useMobileDetection";

const PlayerControlPanel = ({ shortcutName }) => {
  const t1Ref = useRef(null);

  const appReducer = useSelector((state) => state.app);
  const isPlayAll = appReducer.get("isPlayAll");
  const t1 = appReducer.get("t1");
  const pausePlayer = appReducer.get("isPausePlayer");
  const animoPlayerId = appReducer.get("animoPlayerId");
  const duration = useSelector((state) => state.projectDetails.get("duration"));
  const feedbackModal = appReducer.get("feedbackModal");
  const view = feedbackModal.get("view");
  const show = feedbackModal.get("show");
  const dispatch = useDispatch();
  const seekBarRef = useRef();
  const playSeekTimer = useRef();
  const currentTime = useRef();
  const lastTapTimeRef = useRef(0);
  const singleTapTimeoutRef = useRef(null);
  const doubleTapFeedbackTimeoutRef = useRef(null);

  const DOUBLE_TAP_DELAY_MS = 300;
  const SEEK_AMOUNT = 10;
  const DOUBLE_TAP_ICON_DURATION_MS = 700;

  const [percentage, setPercentage] = useState(0);
  const [doubleTapFeedback, setDoubleTapFeedback] = useState(null);
  const [volume, setVolume] = useState(1);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isPausePlayer, setIsPausePlayer] = useState(false);
  const [isShowControl, setIsShowControl] = useState(false);
  const [isIosSafari, setIsIosSafari] = useState(false);
  const isMobile = useMobileDetection();
  const hideControlsTimeoutRef = useRef(null);

  const scheduleHideControls = useCallback(() => {
    if (!isMobile) return;
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      setIsShowControl(false);
      hideControlsTimeoutRef.current = null;
    }, 3000);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && isShowControl) {
      scheduleHideControls();
    }
    return () => {
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isMobile, isShowControl, scheduleHideControls]);

  useEffect(() => {
    // Keep the ref updated with the latest value of t1 for pausing the player on tab visibilitychange.
    t1Ref.current = t1;
  }, [t1]);

  /**
   * Function to pause the player on moving to other tab.
   */
  const handleVisibilityChange = () => {
    if (document.hidden) {
      const playhead = t1Ref.current ? t1Ref.current.time() : 0;
      dispatch(playPausePlayer({ playhead, isPause: true }));
    }
  }

  useEffect(() => {
    setIsIosSafari(isIphoneSafari());
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (singleTapTimeoutRef.current) {
        clearTimeout(singleTapTimeoutRef.current);
      }
      if (doubleTapFeedbackTimeoutRef.current) {
        clearTimeout(doubleTapFeedbackTimeoutRef.current);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsPausePlayer(pausePlayer);
  }, [pausePlayer]);

  useEffect(() => {
    if (isPlayAll && !isPausePlayer) {
      playSeekTimer.current = setInterval(() => {
        // use recent props for interval callback
        const curTime = t1.time();
        currentTime.current = secondsToMinute(curTime);
        setPercentage((curTime / duration) * 100);
      }, 1000 / 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlayAll, isPausePlayer]);

  useEffect(() => {
    if (percentage >= 99) {
      dispatch(showFeedbackModal({ show: true, view: false }));
    }
  }, [percentage, dispatch, view]);

  useEffect(() => {
    if (isPausePlayer) {
      clearInterval(playSeekTimer.current);
    }
  }, [isPausePlayer]);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
      dispatch({
        type: ActionTypes.SET_RESIZE,
      });
    };
    document.addEventListener("fullscreenchange", handleFullScreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePlayPause = () => {
    const playhead = t1 ? t1.time() : 0;
    if (playhead === duration) {
      dispatch(playPausePlayer({ playhead: 0, isPause: false }));
      if (show) dispatch(showFeedbackModal({ show: false, view: false }));
      return;
    }

    if (!isPlayAll && isPausePlayer) {
      dispatch(playPausePlayer({ playhead, isPause: false }));
      dispatch(setPlayAll(true, false));
    } else if (!isPlayAll) {
      // Initial play
      dispatch(setPlayAll(true, false));
    } else if (isPausePlayer && isPlayAll) {
      // Play after pause
      dispatch(playPausePlayer({ playhead, isPause: false }));
      dispatch(showFeedbackModal({ show: false, view: false }));
    } else if (!isPausePlayer && isPlayAll) {
      // Pause
      dispatch(playPausePlayer({ playhead, isPause: true }));
      // Show feedback modal on first pause if feedback not submitted
      dispatch(showFeedbackModal({ show: true, view: false }));
    }
  };

  const seekHandler = (e) => {
    const seekBound = seekBarRef.current?.getBoundingClientRect();
    let seekPercentage = (e.pageX - seekBound.x) / seekBound.width;
    seekPercentage = Math.max(0, Math.min(1, seekPercentage)); // VMK-9372 (Fix for negative percentage)
    const playhead = seekPercentage * duration;
    setPercentage((playhead / duration) * 100);
    currentTime.current = secondsToMinute(playhead);
    dispatch(setPlayHeadTime({ playhead }));
  };

  const handleFullScreen = (fullScreen) => {
    setIsFullScreen(fullScreen);
    dispatch(setAnimoFullScreen({ isFullScreen: fullScreen }));
    dispatch(setPlayAll(false, false));
  };

  const fullScreenHandler = () => {
    const playhead = t1 ? t1.time() : 0;
    dispatch(playPausePlayer({ playhead, isPause: true }));
    if (!document.fullscreenElement) {
      document.getElementById(animoPlayerId).requestFullscreen();
      handleFullScreen(true);
    } else {
      document.exitFullscreen();
      handleFullScreen(false);
    }
  };

  const changeVolumeHandler = (e) => {
    setVolume(e.target.value);
    dispatch(setGlobalVolume({ globalVolume: Number(e.target.value) }));
    dispatch(setPlayHeadTime({ playhead: (t1 && t1.time()) || 0 }));
  };

  const muteHandler = () => {
    let globalVolume;
    if (volume) {
      setVolume(0);
      globalVolume = 0;
    } else {
      setVolume(1);
      globalVolume = 1;
    }
    dispatch(setGlobalVolume({ globalVolume }));
    // Seek the player to update the current volume.
    dispatch(setPlayHeadTime({ playhead: (t1 && t1.time()) || 0 }));
  };

  const handlePlayerTouch = () => {
    if (isMobile) {
      setIsShowControl(true);
    }
  };

  const handleDoubleTapSeek = (isLeftHalf) => {
    if (doubleTapFeedbackTimeoutRef.current) {
      clearTimeout(doubleTapFeedbackTimeoutRef.current);
    }
    setDoubleTapFeedback(isLeftHalf ? "left" : "right");
    doubleTapFeedbackTimeoutRef.current = setTimeout(() => {
      setDoubleTapFeedback(null);
      doubleTapFeedbackTimeoutRef.current = null;
    }, DOUBLE_TAP_ICON_DURATION_MS);
    const currentDur = (t1 && t1.time()) || 0;
    const playhead = isLeftHalf
      ? Math.max(0, currentDur - SEEK_AMOUNT)
      : Math.min(duration, currentDur + SEEK_AMOUNT);
    setPercentage((playhead / duration) * 100);
    currentTime.current = secondsToMinute(playhead);
    dispatch(setPlayHeadTime({ playhead }));
  };

  const handlePlayerTap = (e) => {
    if (!isMobile) {
      handlePlayPause();
      return;
    }
    setIsShowControl(true);
    if (e.target.closest("[data-player-control-bar]")) {
      return;
    }
    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const isLeftHalf = x < rect.width / 2;
    if (now - lastTapTimeRef.current < DOUBLE_TAP_DELAY_MS) {
      clearTimeout(singleTapTimeoutRef.current);
      singleTapTimeoutRef.current = null;
      lastTapTimeRef.current = 0;
      handleDoubleTapSeek(isLeftHalf);
    } else {
      lastTapTimeRef.current = now;
      singleTapTimeoutRef.current = setTimeout(() => {
        singleTapTimeoutRef.current = null;
        handlePlayPause();
      }, DOUBLE_TAP_DELAY_MS);
    }
  };

  const handleShortcuts = (shortcutAction) => {
    switch (shortcutAction) {
      case "START_PLAY_PLAYHEAD_AGAIN":
      case "START_PLAY_PLAYHEAD":
        handlePlayPause();
        break;
      case "MOVE_PLAYHEAD_1S_LEFT_AGAIN":
      case "MOVE_PLAYHEAD_1S_LEFT":
        {
          const currentDur = (t1 && t1.time()) || 0;
          let playhead = (currentDur >= 1 && currentDur - 1) || currentDur;
          setPercentage((playhead / duration) * 100);
          currentTime.current = secondsToMinute(playhead);
          if (playhead <= 1) {
            playhead = 0;
          }
          dispatch(setPlayHeadTime({ playhead }));
        }
        break;
      case "MOVE_PLAYHEAD_1S_RIGHT_AGAIN":
      case "MOVE_PLAYHEAD_1S_RIGHT":
        if (percentage !== 100) {
          const currentDur = t1 && t1.time();
          const playhead =
            currentDur + 1 <= duration ? currentDur + 1 : duration;
          const playheadVal = (playhead / duration) * 100;
          setPercentage(playheadVal);
          currentTime.current = secondsToMinute(playhead);
          dispatch(setPlayHeadTime({ playhead }));
        }
        break;
      case "MOVE_PLAYHEAD_5S_LEFT_AGAIN":
      case "MOVE_PLAYHEAD_5S_LEFT":
        {
          const currentDur = (t1 && t1.time()) || 0;
          const playhead =
            currentDur >= 5 ? currentDur - 5 : currentDur < 5 ? 0 : currentDur;
          setPercentage((playhead / duration) * 100);
          currentTime.current = secondsToMinute(playhead);
          dispatch(setPlayHeadTime({ playhead }));
        }
        break;
      case "MOVE_PLAYHEAD_5S_RIGHT_AGAIN":
      case "MOVE_PLAYHEAD_5S_RIGHT":
        if (percentage !== 100) {
          const currentDur = (t1 && t1.time()) || 5;
          const playhead =
            currentDur + 5 <= duration
              ? currentDur + 5
              : currentDur + 5 > duration
                ? duration
                : currentDur;
          setPercentage((playhead / duration) * 100);
          currentTime.current = secondsToMinute(playhead);
          dispatch(setPlayHeadTime({ playhead }));
        }
        break;
      case "FULL_SCREEN_TOGGLE_AGAIN":
      case "FULL_SCREEN_TOGGLE":
        fullScreenHandler();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (shortcutName && shortcutName !== "") {
      handleShortcuts(shortcutName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shortcutName]);

  const volumeIcon =
    volume === 0 ? `mute` : volume <= 0.49 ? `low-volume` : `high-volume`;
  
  return (
    <>
      <PlayerControls
        onClick={handlePlayerTap}
        onTouchStart={handlePlayerTouch}
        onMouseEnter={() => setIsShowControl(true)}
        onMouseLeave={() => setIsShowControl(false)}
      >
        {!isPlayAll && (
          <BgWrapper>
            <BigPlayButton>
              <Icons src={`${STATIC_PATH}big-play.svg`} alt="big-play" />
            </BigPlayButton>
          </BgWrapper>
        )}
        {isMobile && doubleTapFeedback === "left" && (
          <div
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "50%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 3,
            }}
          >
            <Icons
              src={`${STATIC_PATH}fast-forward-back.svg`}
              alt="rewind"
              width="56px"
              height="56px"
              mobileWidth="56px"
              mobileHeight="56px"
              style={{ opacity: 0.9 }}
            />
          </div>
        )}
        {isMobile && doubleTapFeedback === "right" && (
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: 0,
              width: "50%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "none",
              zIndex: 3,
            }}
          >
            <Icons
              src={`${STATIC_PATH}fast-forward-front.svg`}
              alt="forward"
              width="56px"
              height="56px"
              mobileWidth="56px"
              mobileHeight="56px"
              style={{ opacity: 0.9 }}
            />
          </div>
        )}
      </PlayerControls>
      {isPlayAll && (
        <PlayerContainer
          display={isShowControl ? "flex" : "none"}
          onMouseEnter={() => setIsShowControl(true)}
          onClick={() => isMobile && scheduleHideControls()}
          onTouchStart={() => isMobile && scheduleHideControls()}
        >
          <Icons
            src={`${STATIC_PATH}${isPlayAll && !isPausePlayer ? "animo-pause" : "play-icon"}.svg`}
            id="playIcon"
            alt=""
            onClick={handlePlayPause}
          />
          <PlayTimer>
            {currentTime.current || secondsToMinute(duration)}
          </PlayTimer>
          <SeekBar
            ref={seekBarRef}
            onClick={seekHandler}
            isFullScreen={isFullScreen}
          >
            <Seek width={`${percentage}%`} />
          </SeekBar>
          <VolumeContainer>
            <Icons
              src={`${STATIC_PATH}animo-${volumeIcon}.svg`}
              alt="animo-volume"
              onClick={muteHandler}
            />
            <VolumeSlider className="animo-volume-tool-bar">
              <Slider
                width={"64px"}
                height={"4px"}
                border={"1px solid #DADADC"}
                borderRadius={"2px"}
                background={"#DADADC"}
                margin={"0"}
                thumb={{
                  background: "#637191 0% 0% no-repeat padding-box",
                  border: "none",
                  height: "10px",
                  width: "10px",
                  hoverBG: "#00A7E5",
                  hoverBorder: "1.7px solid #637191",
                }}
                onChangeSliderValue={() => { }}
                onMouseUp={changeVolumeHandler}
                value={volume}
                min={0}
                max={1}
                inputMin={0}
                inputMax={1}
                step={0.01}
                progressBackground={`#00A7E5`}
                isChangeBg={true}
              />
            </VolumeSlider>
          </VolumeContainer>
          {!isIosSafari && <Icons
            src={`${STATIC_PATH}fullscreen${isFullScreen ? "-exit" : ""}.svg`}
            alt=""
            onClick={fullScreenHandler}
          />}
          <Icons
            id="vmaker-icon"
            src={`${STATIC_PATH}vmaker.svg`}
            alt="vmaker-icon"
            cursor="default"
          />
        </PlayerContainer>
      )}
    </>
  );
};

PlayerControlPanel.propTypes = {
  shortcutName: PropTypes.string,
};

export default PlayerControlPanel;
